import { CaretDownIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useId, useState, type ReactNode } from "react";

import { Collapse } from "@/components/Collapse/Collapse";

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  expandedByDefault?: boolean;
  collapsible?: boolean;
  backgroundColor?: "white" | "surface";
};

export function CollapsibleCard({
  title,
  subtitle,
  children,
  expandedByDefault = true,
  collapsible = true,
  backgroundColor = "white",
}: Props) {
  const titleId = useId();
  const contentId = useId();
  const [isExpanded, setIsExpanded] = useState(
    collapsible ? expandedByDefault : true,
  );

  const header = (
    <div>
      <div id={titleId} className="text-base font-medium text-text-primary">
        {title}
      </div>
      {subtitle ? (
        <div className="text-sm text-text-tertiary">{subtitle}</div>
      ) : null}
    </div>
  );

  return (
    <section
      aria-labelledby={titleId}
      className={clsx("rounded-3xl p-4", {
        "bg-white": backgroundColor === "white",
        "bg-surface-background": backgroundColor === "surface",
      })}
    >
      {collapsible ? (
        <button
          type="button"
          className="flex w-full items-center justify-between text-start"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-controls={contentId}
        >
          {header}

          <span className="flex shrink-0 items-center justify-center p-1">
            <CaretDownIcon
              color="inherit"
              size={20}
              weight="bold"
              className={clsx("transition-transform duration-200", {
                "rotate-180": isExpanded,
              })}
            />
          </span>
        </button>
      ) : (
        <div className="flex items-center justify-between">{header}</div>
      )}

      <Collapse id={contentId} in={isExpanded}>
        <div className="pt-4 break-words">{children}</div>
      </Collapse>
    </section>
  );
}
